import client from 'app/client';
import { AddressDto, OperationResponse } from 'api/actor';
import { CreateBrokerCommand } from 'api/broker';

export interface CreatePlateformeThemeDto {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  surfaceColor: string;
  accentColor: string;
  errorColor: string;
  successColor: string;
  warningColor: string;
  infoColor: string;
  fontFamily: string;
}

/** Compte bancaire saisi à la création (le RIB justificatif reste géré après coup). */
export interface CreatePlateformeBankAccountDto {
  iban: string;
  label: string;
  bic?: string | null;
  isDefault: boolean;
}

/**
 * Création d'une plateforme ET de sa personne morale « corporate » principale.
 * Le nom de la plateforme sert de désignation de la PM. ORIAS obligatoire.
 */
export interface CreatePlateformeCommand {
  name: string;
  /** Mode de gestion : GEREE (défaut) ou ADMINISTREE (l'admin pilote). */
  modeGestion?: 'GEREE' | 'ADMINISTREE';
  /** PM existante à lier comme titulaire ; absent = la PM est créée. */
  mainPersonneMoraleId?: string | null;
  /** Cabinet courtier à créer comme titulaire (mode ADMINISTREE sans PM existante).
   * PlateformeIds est posé côté serveur. */
  courtier?: CreateBrokerCommand | null;
  /** CodeEntreprise (clé SSO/leads). Si vide, généré côté serveur (C{année}-{n°}). */
  code?: string | null;
  theme?: CreatePlateformeThemeDto | null;
  address?: AddressDto;
  /** Requis pour le chemin CREER_PM uniquement. */
  phone?: string;
  /** Requis pour le chemin CREER_PM uniquement. */
  registrationNumber?: string;
  capital?: number;
  rcsCity?: string;
  rcsNumber?: string;
  oriasNumber?: string;
  rcp?: string | null;
  /** Email du compte corporate — requis pour le chemin CREER_PM uniquement. */
  email?: string;
  /** Rôles ABP métier en plus d'Plateforme + Assureur (liste blanche serveur). */
  additionalRoles?: string[];
  /** Sous-nature de la PM corporate ('MUT' mutuelle, 'REA' réassureur) — null = compagnie. */
  sousNature?: string | null;
  /** Comptes bancaires (destination des commissions dans les tunnels). */
  bankAccounts?: CreatePlateformeBankAccountDto[];
}

/** Réponse : identifiants du compte corporate généré côté serveur. */
export interface CreatePlateformeResponse extends OperationResponse {
  data?: {
    plateformeId: string;
    /** CodeEntreprise généré (C{année}-{n°}) — clé de résolution SSO/leads. */
    codeEntreprise: string;
    personneMoraleId: string;
    /** Uniquement quand une PM corporate est provisionnée (mode Gérée + création). */
    username?: string;
    temporaryPassword?: string;
  };
}

export async function createPlateforme(
  command: CreatePlateformeCommand,
): Promise<CreatePlateformeResponse> {
  const { data } = await client.post<CreatePlateformeResponse>(
    '/backoffice/plateformes',
    command,
  );
  return data;
}

/** Suggère un CodeEntreprise libre (bouton « Générer » des formulaires org). */
export async function generatePlateformeCode(): Promise<string> {
  const { data } = await client.get<{ code: string }>(
    '/backoffice/plateformes/generate-code',
  );
  return data?.code ?? '';
}

/** PM candidate à la titularité d'une plateforme (jamais déjà titulaire). */
export interface PmEligibleDto {
  id: string;
  designation: string | null;
  nature: string | null;
  natureLibelle: string | null;
  sousNature: string | null;
  sousNatureLibelle: string | null;
}

interface GetPmEligiblesResponse {
  totalCount: number;
  items: PmEligibleDto[];
}

/** Compagnies (CIE) et courtiers grossistes (CRT/GRO) sans plateforme — chemin « PM existante ». */
export async function getPmEligiblesGouvernance(): Promise<PmEligibleDto[]> {
  const { data } = await client.get<GetPmEligiblesResponse>(
    '/backoffice/acteurs/liste/pm-eligibles-gouvernance',
    { params: { MaxResultCount: 500 } },
  );
  return data?.items ?? [];
}

/** Cabinets de courtage (CRT) sans plateforme — chemin « plateforme administrée ». */
export async function getCabinetsEligiblesAdministration(): Promise<PmEligibleDto[]> {
  const { data } = await client.get<GetPmEligiblesResponse>(
    '/backoffice/acteurs/liste/cabinets-eligibles-administration',
    { params: { MaxResultCount: 500 } },
  );
  return data?.items ?? [];
}
